/**
 * DPDP consent manager + erasure flow. Floating tile, no popup spam.
 * Shows all current consents, lets user withdraw or trigger erasure.
 */
import { loadConsents, withdraw, erasureFlow, type ConsentScope, type ConsentRecord } from '../chassis/consent.ts';

const SCOPE_LABEL: Record<ConsentScope, string> = {
  camera_ppg: 'Camera (HRV)',
  microphone_voice: 'Microphone (voice log)',
  mic_ambient_sleep: 'Microphone (ambient sleep)',
  healthkit_read: 'Apple HealthKit read',
  storage_opfs: 'On-device storage',
  payment_razorpay: 'Razorpay checkout',
  mcp_apple_intelligence: 'Apple Intelligence MCP',
  remote_tts_sarvam: 'Sarvam TTS (remote)',
  family_share_e2e: 'Family share (E2E encrypted)',
};

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.55), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(20px, 4vw, 26px);
    color: oklch(94% 0.04 88);
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 14px;
    font-size: 13px;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 9px 0;
    border-bottom: 1px dashed oklch(80% 0.04 80 / 0.1);
    font-family: var(--display);
    font-size: 14px;
    color: oklch(86% 0.04 80);
  }
  li .ts {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: oklch(60% 0.04 60);
  }
  button.withdraw {
    background: transparent;
    color: oklch(80% 0.10 30);
    border: 1px solid oklch(80% 0.10 30 / 0.4);
    padding: 6px 12px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    cursor: pointer;
  }
  .empty {
    font-family: var(--display);
    font-style: italic;
    color: oklch(60% 0.04 60);
    text-align: center;
    padding: 16px;
  }
  .erasure {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid oklch(80% 0.10 30 / 0.2);
    text-align: center;
  }
  button.erase {
    background: oklch(28% 0.13 25);
    color: oklch(94% 0.06 30);
    border: 1px solid oklch(60% 0.18 25 / 0.5);
    padding: 10px 18px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    cursor: pointer;
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · DPDP consent registry</div>
  <h3>Aapne kya allow kiya, kab</h3>
  <div class="sub">DPDP 2023 ke tehat · ek tap par withdraw</div>
  <ul id="list"></ul>
  <div class="erasure">
    <button class="erase" id="erase">Erase all data</button>
  </div>
</div>
`;

export class AramgahConsentModal extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#render();
    const eraseBtn = this.#shadow.getElementById('erase');
    eraseBtn?.addEventListener('click', () => { void this.#erase(); });
  }

  #render(): void {
    const list = this.#shadow.getElementById('list');
    if (!list) return;
    const records = loadConsents();
    const granted = (Object.values(records) as ConsentRecord[]).filter((r) => r.granted);
    if (granted.length === 0) {
      list.innerHTML = '<div class="empty">koi active consent nahi</div>';
      return;
    }
    list.innerHTML = granted.map((r) => `
<li>
  <span>${SCOPE_LABEL[r.scope] ?? r.scope}</span>
  <span class="ts">${shortDate(r.ts)}</span>
  <button class="withdraw" data-scope="${r.scope}">Withdraw</button>
</li>`).join('');
    list.querySelectorAll<HTMLButtonElement>('button.withdraw').forEach((b) => {
      b.addEventListener('click', () => {
        const scope = b.dataset['scope'] as ConsentScope | undefined;
        if (!scope) return;
        withdraw(scope);
        this.#render();
      });
    });
  }

  async #erase(): Promise<void> {
    const ok = confirm('Yeh sab data delete kar dega from this device. Pakka?');
    if (!ok) return;
    await erasureFlow();
    alert('Sab erase ho gaya. Page reload ho raha hai.');
    location.reload();
  }
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

customElements.define('aramgah-consent-modal', AramgahConsentModal);
